/**
 * API Client with JWT Authentication
 * Handles token refresh automatically using httpOnly cookies
 */

import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../store/authStore';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3000';
// If API_URL is a path starting with / (like /api), use it directly
// Otherwise append /api to the base URL
const BASE_URL = API_URL.startsWith('/') ? API_URL : `${API_URL}/api`;

export const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Enable cookies for httpOnly refresh token
  withCredentials: true,
});

// Request interceptor - add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { accessToken } = useAuthStore.getState();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    
    // If FormData, remove Content-Type to let browser set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    
    // If 401 and not already retrying
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const { updateTokens, logout, isAuthenticated } = useAuthStore.getState();
      
      // Only try to refresh if user was authenticated
      if (isAuthenticated) {
        try {
          // Refresh using httpOnly cookie (no body needed)
          // Note: we still support body for backward compatibility during transition
          const response = await axios.post(
            `${BASE_URL}/auth/refresh`,
            {},
            { withCredentials: true }
          );
          
          const { accessToken: newAccessToken } = response.data.data;
          updateTokens(newAccessToken);
          
          // Retry original request
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return api(originalRequest);
        } catch {
          // Refresh failed, logout
          logout();
          window.location.href = '/login';
        }
      } else {
        logout();
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// ============================================
// Auth API
// ============================================
export const authApi = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  
  // Logout - refresh token is sent via httpOnly cookie
  logout: () => api.post('/auth/logout', {}),
  
  // Refresh - uses httpOnly cookie, no body needed
  refresh: () => api.post('/auth/refresh', {}),
  
  me: () => api.get('/auth/me'),
  
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),
};

// ============================================
// Projects API
// ============================================
export const projectsApi = {
  list: (params?: { page?: number; limit?: number; status?: string; search?: string }) =>
    api.get('/projects', { params }),
  
  get: (id: string) => api.get(`/projects/${id}`),
  
  create: (data: { code: string; name: string; line?: string; surveyDate?: string; epsg?: number }) =>
    api.post('/projects', data),
  
  update: (id: string, data: Record<string, unknown>) =>
    api.patch(`/projects/${id}`, data),
  
  delete: (id: string) => api.delete(`/projects/${id}`),
  
  getPhotos: (id: string, params?: { page?: number; limit?: number; elementId?: string; withUrls?: boolean; thumbnail?: boolean }) =>
    api.get(`/projects/${id}/photos`, { params: { ...params, withUrls: params?.withUrls ? 'true' : 'false', thumbnail: params?.thumbnail !== false ? 'true' : 'false' } }),
  
  getElements: (id: string, params?: { page?: number; limit?: number; type?: string }) =>
    api.get(`/projects/${id}/elements`, { params }),
  
  getTrajectory: (id: string) =>
    api.get(`/projects/${id}/trajectory`),
  
  export: (id: string) =>
    api.get(`/projects/${id}/export`),
  
  getAnomalies: (id: string, params?: { page?: number; limit?: number; level?: number; code?: string }) =>
    api.get(`/projects/${id}/anomalies`, { params }),
  
  importElements: (id: string, elements: object[]) =>
    api.post(`/projects/${id}/elements/import`, { elements }),

  importZip: (file: File, onUploadProgress?: (progressEvent: { loaded: number; total?: number }) => void) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/projects/import-zip', formData, {
      onUploadProgress,
    });
  },
};

// ============================================
// Photos API
// ============================================
export const photosApi = {
  get: (id: string) => api.get(`/photos/${id}`),
  
  update: (id: string, data: { elementId?: string | null; camera?: string }) =>
    api.patch(`/photos/${id}`, data),
  
  delete: (id: string) => api.delete(`/photos/${id}`),
  
  upload: (projectId: string, files: FileList | File[]) => {
    const formData = new FormData();
    Array.from(files).forEach((file) => {
      formData.append('files', file);
    });
    return api.post(`/photos/upload/${projectId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  
  getUrl: (id: string, thumbnail = false) =>
    api.get(`/photos/${id}/url`, { params: { thumbnail: thumbnail.toString() } }),

  batchUrls: (photoIds: string[], thumbnail = true, expiresIn = 3600) =>
    api.post('/photos/batch-urls', { photoIds, thumbnail, expiresIn }),

  review: (id: string, isReviewed: boolean) =>
    api.post(`/photos/${id}/review`, { isReviewed }),
};

// ============================================
// Anomalies API
// ============================================
export const anomaliesApi = {
  get: (id: string) => api.get(`/anomalies/${id}`),
  
  getCodes: (params?: { elementType?: string; isActive?: boolean }) =>
    api.get('/anomalies/codes', { params }),
  
  create: (data: {
    photoId: string;
    elementId?: string;
    code: string;
    level: number;
    description?: string;
    geometry: { type: 'point' | 'rectangle' | 'polygon'; coordinates: number[] | number[][] };
  }) => api.post('/anomalies', data),
  
  update: (id: string, data: Record<string, unknown>) => 
    api.patch(`/anomalies/${id}`, data),
  
  delete: (id: string) => api.delete(`/anomalies/${id}`),
  
  bulkCreate: (anomalies: object[]) =>
    api.post('/anomalies/bulk', { anomalies }),
};
